import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const navLinks = [
  { label: "Infrastructure", href: "#trinity" },
  { label: "OE Database", href: "/catalog", external: true },
  { label: "Block Catalog", href: "/engine-block-catalog", external: true },
  { label: "Foundry", href: "/foundry", external: true },
  { label: "Machining", href: "/foundry-machining", external: true },
  { label: "Lubricants", href: "/lubricants", external: true },
  { label: "Industrial Oils", href: "/industrial-oils", external: true },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-lang-switcher]")) setLangOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleNav = (href: string, external?: boolean) => {
    setMenuOpen(false);
    if (external) {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleRuSwitch = () => {
    setLangOpen(false);
    setMenuOpen(false);
    // Route to Russian subdirectory — 1:1 page mapping from root
    window.location.href = "/ru/";
  };

  const isScrolledDark = scrolled;

  return (
    <nav
      className="fixed left-0 right-0 z-50 transition-all duration-300"
      style={{
        top: scrolled ? "0" : "44px",
        background: isScrolledDark ? "rgba(255,255,255,0.98)" : "transparent",
        backdropFilter: isScrolledDark ? "blur(20px)" : "none",
        borderBottom: isScrolledDark ? "1px solid rgba(0,0,0,0.08)" : "none",
      }}
    >
      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between" style={{ height: "72px" }}>

        {/* Logo */}
        <a
          href="#hero"
          onClick={(e) => { e.preventDefault(); handleNav("#hero"); }}
          className="flex items-center gap-3 cursor-pointer shrink-0"
        >
          <div
            className="w-9 h-9 flex items-center justify-center rounded-xl"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" }}
          >
            <i className="ri-settings-3-line text-base text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span
              className="font-black tracking-widest"
              style={{ fontSize: "15px", letterSpacing: "0.18em", color: isScrolledDark ? "#1a2332" : "#ffffff" }}
            >
              CHIRUNNEXA
            </span>
            <span
              className="font-light tracking-widest"
              style={{
                fontSize: "8px",
                letterSpacing: "0.22em",
                color: isScrolledDark ? "rgba(30,45,65,0.4)" : "rgba(255,255,255,0.45)",
                marginTop: "2px",
              }}
            >
              B2B AUTO PARTS PLATFORM
            </span>
          </div>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href, link.external)}
              className="px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{
                color: isScrolledDark ? "rgba(30,45,65,0.6)" : "rgba(255,255,255,0.8)",
                letterSpacing: "0.11em",
                background: "transparent",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = "#D97706";
                (e.currentTarget as HTMLButtonElement).style.background = isScrolledDark ? "rgba(245,158,11,0.08)" : "rgba(255,255,255,0.1)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = isScrolledDark ? "rgba(30,45,65,0.6)" : "rgba(255,255,255,0.8)";
                (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              }}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Right side: Lang switcher + CTA */}
        <div className="hidden lg:flex items-center gap-3">

          {/* Language Switcher */}
          <div className="relative" data-lang-switcher>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 cursor-pointer transition-all duration-200 rounded-xl"
              style={{
                padding: "8px 14px",
                background: isScrolledDark ? "rgba(30,45,65,0.06)" : "rgba(255,255,255,0.15)",
                border: isScrolledDark ? "1.5px solid rgba(30,45,65,0.15)" : "1.5px solid rgba(255,255,255,0.35)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = isScrolledDark ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.22)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = isScrolledDark ? "rgba(30,45,65,0.06)" : "rgba(255,255,255,0.15)";
              }}
            >
              <span style={{ fontSize: "16px", lineHeight: 1 }}>🌐</span>
              <div className="flex flex-col leading-none">
                <span
                  className="font-bold tracking-widest"
                  style={{ fontSize: "11px", letterSpacing: "0.14em", color: isScrolledDark ? "#1a2332" : "#ffffff" }}
                >
                  EN
                </span>
                <span
                  className="font-light"
                  style={{
                    fontSize: "8px",
                    letterSpacing: "0.1em",
                    color: isScrolledDark ? "rgba(30,45,65,0.45)" : "rgba(255,255,255,0.55)",
                    marginTop: "1px",
                  }}
                >
                  GLOBAL
                </span>
              </div>
              <i
                className={`ri-arrow-down-s-line transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`}
                style={{ fontSize: "14px", color: isScrolledDark ? "rgba(30,45,65,0.5)" : "rgba(255,255,255,0.6)" }}
              />
            </button>

            {/* Dropdown */}
            {langOpen && (
              <div
                className="absolute right-0 top-full mt-2 rounded-2xl overflow-hidden z-50"
                style={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.08)", minWidth: "220px" }}
              >
                <div className="px-4 py-3" style={{ borderBottom: "1px solid rgba(0,0,0,0.06)", background: "rgba(0,0,0,0.02)" }}>
                  <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: "rgba(30,45,65,0.4)", letterSpacing: "0.15em", fontSize: "9px" }}>
                    Select Region / Language
                  </p>
                </div>

                {/* EN — current, active */}
                <div
                  className="flex items-center gap-3 px-4 py-3.5"
                  style={{ background: "rgba(245,158,11,0.07)", borderBottom: "1px solid rgba(0,0,0,0.04)" }}
                >
                  <span style={{ fontSize: "22px", lineHeight: 1 }}>🌐</span>
                  <div className="flex-1">
                    <p className="text-sm font-bold" style={{ color: "#D97706" }}>Global (EN)</p>
                    <p className="text-xs font-light" style={{ color: "rgba(30,45,65,0.45)", fontSize: "10px" }}>English · International</p>
                  </div>
                  <div
                    className="w-5 h-5 flex items-center justify-center rounded-full shrink-0"
                    style={{ background: "rgba(245,158,11,0.15)" }}
                  >
                    <i className="ri-check-line text-xs" style={{ color: "#D97706" }} />
                  </div>
                </div>

                {/* RU — routes to /ru/ */}
                <button
                  onClick={handleRuSwitch}
                  className="w-full flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-all duration-150 text-left"
                  style={{ background: "transparent" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(0,0,0,0.03)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                >
                  <span style={{ fontSize: "22px", lineHeight: 1 }}>🇷🇺</span>
                  <div className="flex-1">
                    <p className="text-sm font-bold" style={{ color: "#1a2332" }}>СНГ (RU)</p>
                    <p className="text-xs font-light" style={{ color: "rgba(30,45,65,0.45)", fontSize: "10px" }}>Русский · Евразийский рынок</p>
                  </div>
                  <i className="ri-external-link-line text-xs" style={{ color: "rgba(30,45,65,0.35)" }} />
                </button>
              </div>
            )}
          </div>

          {/* CTA Button */}
          <button
            onClick={() => handleNav("#contact")}
            className="flex items-center gap-2 rounded-xl font-bold tracking-widest transition-all duration-200 hover:brightness-110 active:scale-95 cursor-pointer text-white whitespace-nowrap"
            style={{
              padding: "10px 22px",
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              letterSpacing: "0.13em",
              fontSize: "12px",
            }}
          >
            <i className="ri-mail-send-line text-sm" />
            GET QUOTE
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            background: isScrolledDark ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.12)",
            border: isScrolledDark ? "1px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <i
            className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg`}
            style={{ color: isScrolledDark ? "#D97706" : "#ffffff" }}
          />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="lg:hidden"
          style={{ background: "rgba(255,255,255,0.99)", borderBottom: "1px solid rgba(0,0,0,0.08)" }}
        >
          {/* Lang switcher — mobile top */}
          <div className="px-5 pt-4 pb-3 flex gap-3" style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
            {/* EN — active */}
            <div
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold"
              style={{
                background: "rgba(245,158,11,0.1)",
                border: "2px solid rgba(245,158,11,0.5)",
                color: "#D97706",
              }}
            >
              <span style={{ fontSize: "18px" }}>🌐</span>
              <span className="tracking-wide">Global (EN)</span>
            </div>
            {/* RU — routes to /ru/ */}
            <button
              onClick={handleRuSwitch}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all duration-200"
              style={{
                background: "rgba(0,0,0,0.04)",
                border: "2px solid rgba(0,0,0,0.06)",
                color: "rgba(30,45,65,0.6)",
              }}
            >
              <span style={{ fontSize: "18px" }}>🇷🇺</span>
              <span className="tracking-wide">СНГ (RU)</span>
            </button>
          </div>

          {/* Nav links */}
          <div className="px-5 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNav(link.href, link.external)}
                className="text-left px-4 py-3 rounded-xl text-sm font-semibold uppercase tracking-widest cursor-pointer transition-all duration-150 whitespace-nowrap"
                style={{ color: "rgba(30,45,65,0.7)", letterSpacing: "0.13em" }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "rgba(245,158,11,0.07)";
                  (e.currentTarget as HTMLButtonElement).style.color = "#D97706";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                  (e.currentTarget as HTMLButtonElement).style.color = "rgba(30,45,65,0.7)";
                }}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Mobile CTA */}
          <div className="px-5 pb-5">
            <button
              onClick={() => handleNav("#contact")}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-xl text-sm font-bold tracking-widest cursor-pointer text-white transition-all hover:brightness-110 whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.14em" }}
            >
              <i className="ri-mail-send-line" />
              GET QUOTE
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
